import { ref } from 'vue'

/**
 * UI theme (Account settings > Preferences).
 *
 * Same plumbing as the date format (`utils/dateFormatPreference.ts`): the choice
 * is saved on the account (`User.theme`) and re-applied by `services/session.ts`
 * after every `fetchUser()`; `localStorage` is a cache so a guest, or a reload
 * before `getUser` answers, gets the last known theme.
 *
 * The theme is applied as `data-theme` on `<html>`: every colour goes through
 * the tokens in `assets/main.css`, which `assets/themes.css` redefines per theme.
 * ⚠️ `public/theme-boot.js` reads the same `localStorage` key and sets the
 * attribute **before first paint** (the CSP forbids inline scripts): change the
 * key or the values there too.
 */
export type ThemePreference = 'classic' | 'dark' | 'dim'

export const THEME_PREFERENCES: readonly ThemePreference[] = ['classic', 'dark', 'dim']

export const DEFAULT_THEME: ThemePreference = 'classic'

const STORAGE_KEY = 'travick.theme'

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_PREFERENCES as readonly string[]).includes(value)
}

function storedTheme(): ThemePreference | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return isThemePreference(raw) ? raw : null
  } catch {
    return null
  }
}

/**
 * Active theme. A module-level `ref` like `dateFormat`: charts read it too,
 * because Chart.js paints on a canvas and cannot see CSS variables, so they have
 * to redraw when it changes.
 */
export const theme = ref<ThemePreference>(storedTheme() ?? DEFAULT_THEME)

function paint(value: ThemePreference): void {
  document.documentElement.dataset.theme = value
}

/**
 * Shows a theme without remembering it: the live preview of the picker in the
 * settings, which falls back to the saved theme when left without saving.
 */
export function previewTheme(value: ThemePreference): void {
  theme.value = value
  paint(value)
}

export function setTheme(value: ThemePreference): void {
  previewTheme(value)
  try {
    window.localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // Not fatal: the choice just won't survive a reload.
  }
}

/** The theme remembered in this browser: what a cancelled preview goes back to. */
export function savedTheme(): ThemePreference {
  return storedTheme() ?? DEFAULT_THEME
}

/**
 * Aligns the theme with the one saved on the account, if any. An account that
 * never chose (`null`) keeps this browser's theme, like the date format.
 */
export function applyAccountTheme(value: string | null | undefined): void {
  if (isThemePreference(value)) setTheme(value)
}

/** Called by `main.ts` before mount, in case `theme-boot.js` did not run. */
export function hydrateTheme(): void {
  paint(theme.value)
}
